import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Search,
  Plus,
  Users,
  Eye,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Calendar,
  RotateCcw,
  Banknote,
  Phone,
  MapPin,
  StickyNote,
  CreditCard,
  Wallet,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import DateFilter from '../components/DateFilter';
import { useAuth } from '../context/AuthContext';

const avatarColors = [
  'bg-emerald-100 text-emerald-700',
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

export default function Staff() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editStaff, setEditStaff] = useState(null);
  const [deleteStaff, setDeleteStaff] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    phone: '',
    address: '',
    age: '',
    salary_amount: '0',
    advanced_payment: '0',
    payment_method: 'cash',
    notes: '',
  });

  const [paySalaryStaff, setPaySalaryStaff] = useState(null);
  const [salaryForm, setSalaryForm] = useState({
    amount: '',
    deduction_amount: '0',
    payment_method: 'cash',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [payAdvanceStaff, setPayAdvanceStaff] = useState(null);
  const [advanceForm, setAdvanceForm] = useState({ amount: '', payment_method: 'cash', date: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      let url = '/staff?limit=500';
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await api.get(url);
      const list = Array.isArray(res) ? res : (res?.staff || []);
      setStaffList(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load staff');
    }
  };

  const filtered = staffList.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.phone && s.phone.includes(search))
  );

  const totalSalary = filtered.reduce((sum, s) => sum + (parseFloat(s.salary_amount) || 0), 0);
  const totalAdvanced = filtered.reduce((sum, s) => sum + (parseFloat(s.advanced_payment) || 0), 0);

  const resetFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
  };

  const applyDateFilter = () => {
    loadStaff();
  };

  const openAdd = () => {
    setAddForm({ name: '', phone: '', address: '', age: '', salary_amount: '0', advanced_payment: '0', payment_method: 'cash', notes: '' });
    setShowAdd(true);
  };

  const saveNew = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) return;
    try {
      await api.post('/staff', {
        name: addForm.name,
        phone: addForm.phone,
        address: addForm.address,
        age: addForm.age ? parseInt(addForm.age) : null,
        salary_amount: parseFloat(addForm.salary_amount) || 0,
        advanced_payment: parseFloat(addForm.advanced_payment) || 0,
        payment_method: addForm.payment_method || 'cash',
        notes: addForm.notes,
      });
      toast.success('Staff added successfully');
      setShowAdd(false);
      loadStaff();
    } catch (err) {
      toast.error(err.message || 'Failed to add staff');
    }
  };

  const openEdit = (staff) => {
    setEditStaff(staff);
    setEditForm({ ...staff, age: staff.age || '' });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/staff/${editForm.id}`, {
        name: editForm.name,
        phone: editForm.phone,
        address: editForm.address,
        age: editForm.age ? parseInt(editForm.age) : null,
        salary_amount: parseFloat(editForm.salary_amount) || 0,
        advanced_payment: parseFloat(editForm.advanced_payment) || 0,
        notes: editForm.notes,
      });
      toast.success('Staff updated successfully');
      setEditStaff(null);
      loadStaff();
    } catch (err) {
      toast.error(err.message || 'Failed to update staff');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/staff/${deleteStaff.id}`);
      toast.success('Staff deleted');
      setDeleteStaff(null);
      loadStaff();
    } catch (err) {
      toast.error(err.message || 'Failed to delete staff');
    }
  };

  const openPaySalary = (staff) => {
    setPaySalaryStaff(staff);
    const base = parseFloat(staff.salary_amount) || 0;
    const adv = parseFloat(staff.advanced_payment) || 0;
    const ded = adv > 0 ? Math.min(adv, base) : 0;
    setSalaryForm({
      amount: staff.salary_amount || '0',
      deduction_amount: ded > 0 ? String(ded) : '0',
      payment_method: 'cash',
      date: new Date().toISOString().split('T')[0],
      notes: `Salary payment for ${staff.name}`,
    });
  };

  const submitSalaryPayment = async (e) => {
    e.preventDefault();
    const gross = parseFloat(salaryForm.amount);
    const deduction = parseFloat(salaryForm.deduction_amount) || 0;
    if (!gross || gross <= 0) return toast.error('Enter a valid gross salary amount');
    if (deduction > gross) return toast.error('Advance deduction cannot exceed gross salary');
    const currentAdv = parseFloat(paySalaryStaff?.advanced_payment) || 0;
    if (deduction > currentAdv) return toast.error(`Advance deduction cannot exceed active advance of Rs. ${currentAdv.toLocaleString('en-PK')}`);

    const net = Math.max(0, gross - deduction);
    try {
      await api.post('/staff-transactions', {
        staff_id: paySalaryStaff.id,
        type: 'salary',
        amount: gross,
        deduction_amount: deduction,
        payment_method: salaryForm.payment_method,
        transaction_date: salaryForm.date,
        notes: salaryForm.notes || `Salary payment for ${paySalaryStaff.name}`,
        user_id: user?.id || 1,
      });
      toast.success(`Rs. ${net.toLocaleString('en-PK')} net salary paid (${salaryForm.payment_method === 'cash' ? 'Cash' : 'Bank'})`);
      setPaySalaryStaff(null);
      loadStaff();
    } catch (err) {
      toast.error(err.message || 'Failed to record salary payment');
    }
  };

  const openPayAdvance = (staff) => {
    setPayAdvanceStaff(staff);
    setAdvanceForm({
      amount: '',
      payment_method: 'cash',
      date: new Date().toISOString().split('T')[0],
      notes: `Advance payment for ${staff.name}`,
    });
  };

  const submitAdvancePayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(advanceForm.amount);
    if (!amount || amount <= 0) return toast.error('Enter a valid amount');
    try {
      await api.post('/staff-transactions', {
        staff_id: payAdvanceStaff.id,
        type: 'advance',
        amount,
        payment_method: advanceForm.payment_method,
        transaction_date: advanceForm.date,
        notes: advanceForm.notes || `Advance payment for ${payAdvanceStaff.name}`,
        user_id: user?.id || 1,
      });
      toast.success(`Rs. ${amount.toLocaleString('en-PK')} advance paid from ${advanceForm.payment_method === 'cash' ? 'Cash' : 'Bank'}`);
      setPayAdvanceStaff(null);
      loadStaff();
    } catch (err) {
      toast.error(err.message || 'Failed to record advance payment');
    }
  };

  const defaultFormFields = (
    <>
      <div className="col-span-2">
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Staff Name *</label>
        <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
      </div>
      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone Number</label>
        <input value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
      </div>
      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Age</label>
        <input type="number" value={addForm.age} onChange={(e) => setAddForm({ ...addForm, age: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
      </div>
      <div className="col-span-2">
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label>
        <input value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
      </div>
      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Salary Amount (Rs.)</label>
        <input type="number" step="0.01" value={addForm.salary_amount} onChange={(e) => setAddForm({ ...addForm, salary_amount: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
      </div>
      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advanced Payment (Rs.)</label>
        <input type="number" step="0.01" value={addForm.advanced_payment} onChange={(e) => setAddForm({ ...addForm, advanced_payment: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
      </div>
      {parseFloat(addForm.advanced_payment) > 0 && (
        <div className="col-span-2">
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance Paid From</label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setAddForm({ ...addForm, payment_method: 'cash' })}
              className={clsx(
                'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-all',
                addForm.payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600'
              )}
            >
              <Wallet className="h-3.5 w-3.5" /> Cash Drawer
            </button>
            <button
              type="button"
              onClick={() => setAddForm({ ...addForm, payment_method: 'bank' })}
              className={clsx(
                'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-all',
                addForm.payment_method === 'bank' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600'
              )}
            >
              <CreditCard className="h-3.5 w-3.5" /> Bank Account
            </button>
          </div>
        </div>
      )}
      <div className="col-span-2">
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note</label>
        <textarea value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
        <p className="mt-1 text-sm text-gray-500">Manage staff profiles, salaries, and advanced payments.</p>
      </div>

      {/* ── Top Actions ── */}
      <div className="flex flex-wrap items-center justify-end gap-4">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-sm">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
            <Banknote className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Total Salary</p>
            <p className="text-sm font-bold text-emerald-600">Rs. {totalSalary.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]">
          <span className="text-lg leading-none">+</span> Add Staff
        </button>
      </div>

      {/* ── Search & Date Filter ── */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff by name or phone..." className="w-full rounded-lg border border-gray-300 bg-white py-2.5 px-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
        </div>
      </div>

      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onApply={applyDateFilter}
        onReset={resetFilters}
      />

      {/* ── Table ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-500" />
            <h2 className="text-base font-semibold text-gray-900">Staff Members</h2>
          </div>
          <span className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            TOTAL : {filtered.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Staff Name</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Phone</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Address</th>
                <th className="px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Age</th>
                <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Salary</th>
                <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Advanced</th>
                <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((s, idx) => (
                <tr key={s.id} className="group transition-colors hover:bg-gray-50/50">
                  <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                    <Link to={`/staff/${s.id}`} className="flex items-center gap-3 hover:opacity-80">
                      <div className={clsx('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold', avatarColors[idx % avatarColors.length])}>
                        {s.name.charAt(0)}
                      </div>
                      <span className="text-sm font-semibold text-gray-900 hover:text-emerald-600 transition-colors">{s.name}</span>
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{s.phone || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[200px] truncate">{s.address || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">{s.age || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-emerald-600">Rs. {(parseFloat(s.salary_amount) || 0).toLocaleString('en-PK')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-amber-600">Rs. {(parseFloat(s.advanced_payment) || 0).toLocaleString('en-PK')}</td>
                  <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => openPaySalary(s)} title="Pay Salary" className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"><Banknote className="h-4 w-4" /></button>
                      <button onClick={() => openPayAdvance(s)} title="Give Advance" className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600"><CreditCard className="h-4 w-4" /></button>
                      <Link to={`/staff/${s.id}`} title="View Details & Ledger" className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"><Eye className="h-4 w-4" /></Link>
                      <button onClick={() => openEdit(s)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => setDeleteStaff(s)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Users className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm">No staff members found. Click "Add Staff" to get started.</p>
          </div>
        )}
      </div>

      {/* ── ADD MODAL ── */}
      {showAdd &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Add New Staff</h3>
                <button onClick={() => setShowAdd(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveNew} className="px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  {defaultFormFields}
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Staff</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ── EDIT MODAL ── */}
      {editStaff &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditStaff(null)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Staff</h3>
                <button onClick={() => setEditStaff(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveEdit} className="px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Staff Name</label>
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone Number</label>
                    <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Age</label>
                    <input type="number" value={editForm.age} onChange={(e) => setEditForm({ ...editForm, age: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label>
                    <input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Salary Amount (Rs.)</label>
                    <input type="number" step="0.01" value={editForm.salary_amount} onChange={(e) => setEditForm({ ...editForm, salary_amount: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advanced Payment (Rs.)</label>
                    <input type="number" step="0.01" value={editForm.advanced_payment} onChange={(e) => setEditForm({ ...editForm, advanced_payment: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note</label>
                    <textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setEditStaff(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Changes</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {deleteStaff &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteStaff(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Staff Member?</h3>
              <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete <strong>{deleteStaff.name}</strong>? This action cannot be undone.</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setDeleteStaff(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmDelete} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ── PAY SALARY MODAL ── */}
      {paySalaryStaff &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPaySalaryStaff(null)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                    <Banknote className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Pay Salary</h3>
                    <p className="text-xs text-gray-500">{paySalaryStaff.name}</p>
                  </div>
                </div>
                <button onClick={() => setPaySalaryStaff(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={submitSalaryPayment} className="px-6 py-5 space-y-4">
                {/* Salary and Advance Info Header */}
                <div className="flex items-center justify-between rounded-xl bg-gray-50 p-3 border border-gray-200/60 text-xs">
                  <div>
                    <span className="text-gray-500 block">Monthly Base:</span>
                    <span className="font-bold text-gray-800">
                      Rs. {(parseFloat(paySalaryStaff.salary_amount) || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-500 block">Active Advance:</span>
                    <span className={clsx('font-bold', parseFloat(paySalaryStaff.advanced_payment) > 0 ? 'text-amber-600' : 'text-emerald-600')}>
                      Rs. {(parseFloat(paySalaryStaff.advanced_payment) || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Gross Salary (Rs.) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={salaryForm.amount}
                    onChange={(e) => setSalaryForm({ ...salaryForm, amount: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Advance Deduction if staff has active advance */}
                {parseFloat(paySalaryStaff.advanced_payment) > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        Deduct from Advance (Rs.)
                      </label>
                      <span className="text-[11px] text-amber-600 font-semibold">
                        Max: Rs. {(parseFloat(paySalaryStaff.advanced_payment) || 0).toLocaleString('en-PK')}
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={parseFloat(paySalaryStaff.advanced_payment) || 0}
                      value={salaryForm.deduction_amount}
                      onChange={(e) => setSalaryForm({ ...salaryForm, deduction_amount: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                )}

                {/* Net Disbursed Preview Banner */}
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-gray-600 block">Net Payout (Cash/Bank Outflow):</span>
                    <span className="text-base font-extrabold text-emerald-700">
                      Rs. {Math.max(0, (parseFloat(salaryForm.amount) || 0) - (parseFloat(salaryForm.deduction_amount) || 0)).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  {parseFloat(paySalaryStaff.advanced_payment) > 0 && (
                    <div className="text-right">
                      <span className="text-gray-500 block">Remaining Advance:</span>
                      <span className="font-bold text-amber-700">
                        Rs. {Math.max(0, (parseFloat(paySalaryStaff.advanced_payment) || 0) - (parseFloat(salaryForm.deduction_amount) || 0)).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Method *</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setSalaryForm({ ...salaryForm, payment_method: 'cash' })}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all',
                        salaryForm.payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      )}
                    >
                      <Wallet className="h-4 w-4" /> Cash Drawer
                    </button>
                    <button
                      type="button"
                      onClick={() => setSalaryForm({ ...salaryForm, payment_method: 'bank' })}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all',
                        salaryForm.payment_method === 'bank' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      )}
                    >
                      <CreditCard className="h-4 w-4" /> Bank Account
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date *</label>
                  <input
                    type="date"
                    value={salaryForm.date}
                    onChange={(e) => setSalaryForm({ ...salaryForm, date: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                  <textarea
                    value={salaryForm.notes}
                    onChange={(e) => setSalaryForm({ ...salaryForm, notes: e.target.value })}
                    rows={2}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setPaySalaryStaff(null)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                  >
                    <Banknote className="h-4 w-4" /> Confirm & Pay Salary
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ── GIVE ADVANCE MODAL ── */}
      {payAdvanceStaff &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPayAdvanceStaff(null)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                    <CreditCard className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Give Advance</h3>
                    <p className="text-xs text-gray-500">{payAdvanceStaff.name}</p>
                  </div>
                </div>
                <button onClick={() => setPayAdvanceStaff(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={submitAdvancePayment} className="px-6 py-5 space-y-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Amount (Rs.) *</label>
                  <input type="number" step="0.01" min="0" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Method *</label>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setAdvanceForm({ ...advanceForm, payment_method: 'cash' })} className={clsx('flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all', advanceForm.payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300')}>
                      <Wallet className="h-4 w-4" /> Cash
                    </button>
                    <button type="button" onClick={() => setAdvanceForm({ ...advanceForm, payment_method: 'bank' })} className={clsx('flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all', advanceForm.payment_method === 'bank' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300')}>
                      <CreditCard className="h-4 w-4" /> Bank
                    </button>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date *</label>
                  <input type="date" value={advanceForm.date} onChange={(e) => setAdvanceForm({ ...advanceForm, date: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                  <textarea value={advanceForm.notes} onChange={(e) => setAdvanceForm({ ...advanceForm, notes: e.target.value })} rows={2} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
                </div>
                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button type="button" onClick={() => setPayAdvanceStaff(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-600">
                    <CreditCard className="h-4 w-4" /> Give Advance
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
